/**
 * 並べ替えの業務ルールの中身 — キーの表（母集合 ・ 値が無いとき ・ 同順位 ・ 出す件数は ⓘ）と、キーの決め方
 * （キーごとに判定表か式でキーの値を決める。設計書 api §3.3 の `derive`）。
 *
 * **キーの決め方は案件の突き合わせでキーごとに揃える。** 業務ルールの画面はキーごとに左右を 1 段に並べ、
 * 片側に無いキーは反対側を斜線の空札にする（`alignDeriveKeys` で揃える）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 並べ替えのキーの表と読み方の ⓘ・キーの決め方を描き、案件で足した・外したキーの決め方をその側の色で塗る。
 * @graph-connects DecisionTableBody [calls] キーの決め方の判定表
 * @graph-connects FormulaLines [calls] キーの決め方の式
 */
import type { ElementRef, RuleBodyView, RuleView } from '@cortex/domain-spec-console';
import { useTranslation } from 'react-i18next';

import type { DiffSide } from '../components/spec/DiffSplitPanes.js';
import { ElementTermLink } from '../components/spec/TermText.js';
import {
  SPEC_DIFF_WORD_CLASS,
  specDiffCellClass,
  type SpecDiffState,
} from '../components/spec/SpecDiffState.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import {
  DecisionTableBody,
  ElementRefText,
  FormulaLines,
  HEAD_CELL_CLASS,
  isMissingOn,
  MUTED_CLASS,
  VALUE_CELL_CLASS,
  type RuleCompareProps,
} from './rule-body-parts.js';
import { InfoMark } from './screen-detail-cells.js';

/** 並べ替えの中身。 @graph-connects none */
type SortBodyView = Extract<RuleBodyView, { kind: 'sort' }>;

/**
 * 並べ替えのキー 1 つの決め方。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 並べ替えのキー 1 つの決め方（キーと、判定表か式の中身）
 * @graph-connects none
 */
export type SortDeriveView = SortBodyView['derive'][number];

/** 並べ替えのキーの属性の番号を並べる。並べ替えでなければ空。 @graph-connects none */
function sortAttributeIds(rule: RuleView | null | undefined): number[] | undefined {
  if (rule === null || rule === undefined) return undefined;
  return rule.body.kind === 'sort' ? rule.body.keys.map((key) => key.attribute.id) : [];
}

/**
 * 業務ルールのキーの決め方を並べる。並べ替えでなければ空。
 *
 * @param rule 業務ルール。無ければ空
 * @returns キーの決め方の並び
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの中身が並べ替えなら、そのキーの決め方の並びを返す。
 * @graph-connects none
 */
export function sortDeriveOf(rule: RuleView | null | undefined): readonly SortDeriveView[] {
  return rule?.body.kind === 'sort' ? rule.body.derive : [];
}

/**
 * キーの決め方の、案件での扱い。反対側の並べ替えに同じキーの決め方が無ければ、変更後の面では追加、
 * 変更前の面では削除。案件を選んでいない・反対側の業務ルールが無いときは印を付けない。
 *
 * @param keyId キーの番号
 * @param side 描いている面
 * @param counterpart 反対側の姿
 * @returns 追加 / 削除 / 変わっていない
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business キーの決め方 1 つが、反対側の業務ルールに無いかで追加・削除の印を決める。
 * @graph-connects sortDeriveOf [calls] 反対側のキーの決め方
 */
export function deriveMarkOf(
  keyId: number,
  side: DiffSide | undefined,
  counterpart: RuleView | null | undefined,
): SpecDiffState {
  if (side === undefined || counterpart === undefined || counterpart === null) return 'unchanged';
  if (sortDeriveOf(counterpart).some((derive) => derive.key.id === keyId)) return 'unchanged';
  return side === 'after' ? 'created' : 'deleted';
}

/**
 * 変更前と変更後のキーの決め方を、キーで揃えた対の並びにする。両側にあるキーは最長共通部分列で
 * 揃え、片側だけのキーは元の位置に差し込む（間に挟まったものは 変更前だけ → 変更後だけ の順）。
 *
 * @param before 変更前のキーの決め方
 * @param after 変更後のキーの決め方
 * @returns キーと、その側のキーの決め方（無ければ null）の対の並び
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後のキーの決め方を、キーごとに左右が同じ段に来る並びへ揃える。
 * @graph-connects none
 */
export function alignDeriveKeys(
  before: readonly SortDeriveView[],
  after: readonly SortDeriveView[],
): {
  key: ElementRef;
  before: SortDeriveView | null;
  after: SortDeriveView | null;
}[] {
  // lengths[i][j] ＝ before[i..] と after[j..] の最長共通部分列の長さ
  const lengths = Array.from({ length: before.length + 1 }, () =>
    Array.from({ length: after.length + 1 }, () => 0),
  );
  const at = (i: number, j: number): number => lengths[i]?.[j] ?? 0;
  for (let i = before.length - 1; i >= 0; i -= 1) {
    for (let j = after.length - 1; j >= 0; j -= 1) {
      const row = lengths[i];
      if (row === undefined) continue;
      row[j] =
        before[i]?.key.id === after[j]?.key.id
          ? at(i + 1, j + 1) + 1
          : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  const pairs: { key: ElementRef; before: SortDeriveView | null; after: SortDeriveView | null }[] =
    [];
  const onlyBefore: SortDeriveView[] = [];
  const onlyAfter: SortDeriveView[] = [];
  const flush = () => {
    for (const entry of onlyBefore) pairs.push({ key: entry.key, before: entry, after: null });
    for (const entry of onlyAfter) pairs.push({ key: entry.key, before: null, after: entry });
    onlyBefore.length = 0;
    onlyAfter.length = 0;
  };
  let i = 0;
  let j = 0;
  while (i < before.length || j < after.length) {
    const left = before[i];
    const right = after[j];
    if (left !== undefined && right !== undefined && left.key.id === right.key.id) {
      flush();
      pairs.push({ key: right.key, before: left, after: right });
      i += 1;
      j += 1;
    } else if (left !== undefined && (right === undefined || at(i + 1, j) >= at(i, j + 1))) {
      onlyBefore.push(left);
      i += 1;
    } else if (right !== undefined) {
      onlyAfter.push(right);
      j += 1;
    }
  }
  flush();
  return pairs;
}

/** `SortDeriveEntry` の props。 @graph-connects none */
interface SortDeriveEntryProps {
  /** キー 1 つの決め方。 */
  derive: SortDeriveView;
  /** 描いている面。案件を選んでいなければ省く。 */
  side?: DiffSide;
  /** 案件での扱い（追加 / 削除なら小見出しのキーを濃い色で、中身を地色で塗る）。 */
  mark: SpecDiffState;
}

/**
 * キー 1 つの決め方 — キーの小見出しと、判定表か式。判定表の出力の列はキーそのもの。
 *
 * 案件で足した・外したキーは、小見出しのキーをその側の濃い色（変わった語と同じ段）で、中身を地色で
 * 塗る。「追加」「削除」の札は付けない（Dhik 2026-09-29「セル内の変わったところの色を濃くするで統一
 * してほしい」）。札の語は小見出しを指すと出す文字にだけ残す。
 *
 * @param props キーの決め方・描いている面・案件での扱い
 * @returns キー 1 つの決め方
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business キー 1 つの決め方を小見出しと判定表か式で描き、案件で足した・外したものは小見出しのキーを濃い色で、中身を地色で塗る。
 * @graph-connects DecisionTableBody [calls] 判定表
 * @graph-connects FormulaLines [calls] 式
 */
export function SortDeriveEntry({ derive, side = 'after', mark }: SortDeriveEntryProps) {
  const { t } = useTranslation();
  const marked = mark !== 'unchanged';
  const tone = marked ? specDiffCellClass(true, side) : '';
  const { body } = derive;
  return (
    <div className="flex flex-col gap-1.5" data-derive-key={derive.key.id} data-diff-state={mark}>
      <h4 className="inline-flex flex-wrap items-center gap-2 text-sm font-bold text-[var(--color-text-primary)]">
        <span
          className={marked ? SPEC_DIFF_WORD_CLASS[side] : undefined}
          title={marked ? t(`specDiff.${mark}`) : undefined}
        >
          <ElementRefText value={derive.key} />
        </span>
      </h4>
      {body.kind === 'decisionTable' ? (
        <DecisionTableBody
          body={{
            kind: 'decisionTable',
            inputs: body.inputs,
            outputs: [derive.key],
            rows: body.rows.map((row) => ({ id: row.id, cells: row.cells, outputs: [row.output] })),
            overlapPolicy: body.overlapPolicy,
          }}
          rowClass={tone}
        />
      ) : (
        <FormulaLines steps={body.steps} className={tone} />
      )}
    </div>
  );
}

/** `SortDeriveList` の props。 @graph-connects none */
interface SortDeriveListProps {
  /** キーの決め方の並び。 */
  derive: readonly SortDeriveView[];
}

/**
 * キーの決め方をキーの順に 1 面で並べる（業務ルールの画面の畳みの中）。案件の突き合わせは左右の段を
 * キーで揃える側（`alignDeriveKeys` と `SortDeriveEntry`）が持つので、ここは印を付けない。
 *
 * @param props キーの決め方
 * @returns キーの決め方の並び
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 並べ替えのキーの決め方をキーごとに 1 面で並べる。
 * @graph-connects SortDeriveEntry [calls] キー 1 つの決め方
 */
export function SortDeriveList({ derive }: SortDeriveListProps) {
  return (
    <div className="flex flex-col gap-4" data-derive-list={derive.length}>
      {derive.map((entry) => (
        <SortDeriveEntry key={entry.key.id} derive={entry} mark="unchanged" />
      ))}
    </div>
  );
}

/** 文の型紙の中でキーが入る位置の印。訳してからこの印で割り、間にキーのリンクを差し込む。 @graph-connects none */
const KEY_SLOT = '\u0000';

/** `DeriveAbsentCard` の props。 @graph-connects none */
interface DeriveAbsentCardProps {
  /** 反対側にだけあるキー。 */
  keyRef: ElementRef;
  /** 反対側で足した（`created`）か、外した（`deleted`）か。 */
  state: 'created' | 'deleted';
}

/**
 * 片側に無いキーの決め方の空札。斜線の地で「無い」ことを示し、この案件で足したか外したかを文で添える。
 *
 * @param props キーと、足したか外したか
 * @returns 斜線の空札
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 片側にしか無いキーの決め方の位置を、斜線の空札と足した・外したの文で占め、文中のキーが用語なら用語詳細へ繋ぐ。
 * @graph-connects displayLabel [calls] キーの表示名
 * @graph-connects ElementTermLink [calls] 文中のキーの用語リンク
 */
export function DeriveAbsentCard({ keyRef, state }: DeriveAbsentCardProps) {
  const { t } = useTranslation();
  const key = displayLabel(keyRef.label, t, keyRef.untranslated);
  /* 文の型紙をキーの位置で割り、キーだけを用語詳細へ繋ぐ。語順は訳が持つので画面では決めない。 */
  const [head = '', tail = ''] = t(
    state === 'created' ? 'businessLogic.deriveAddedInPj' : 'businessLogic.deriveRemovedInPj',
    { key: KEY_SLOT },
  ).split(KEY_SLOT);
  return (
    <div
      data-derive-absent={state}
      className="grid min-h-24 place-content-center gap-1 border border-dashed border-[var(--color-border)] bg-[repeating-linear-gradient(135deg,var(--color-border)_0,var(--color-border)_1px,transparent_1px,transparent_8px)] px-3 py-2 text-center text-xs text-[var(--color-text-muted)]"
    >
      <span className="font-bold">{t('businessLogic.deriveAbsent')}</span>
      <span>
        {head}
        <ElementTermLink id={keyRef.id} title={key}>
          {key}
        </ElementTermLink>
        {tail}
      </span>
    </div>
  );
}

/** 並べ替えのキー 1 つ。 @graph-connects none */
type SortKeyView = SortBodyView['keys'][number];

/**
 * 並べ替えのキーを、値が無いときの扱いごとに束ねる。束の並びは、その扱いが最初に出るキーの順。
 *
 * @param keys 並べ替えのキー（順位の順）
 * @returns 扱いと、その扱いのキーの並び
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 並べ替えのキーを値が無いときの扱い（除く・末尾へ・先頭へ）ごとに束ね、表の下の 1 行に出せる形にする。
 * @graph-connects none
 */
export function groupWhenMissing(
  keys: readonly SortKeyView[],
): { policy: SortKeyView['whenMissing']; keys: ElementRef[] }[] {
  const groups: { policy: SortKeyView['whenMissing']; keys: ElementRef[] }[] = [];
  for (const key of keys) {
    const group = groups.find((entry) => entry.policy === key.whenMissing);
    if (group === undefined) groups.push({ policy: key.whenMissing, keys: [key.attribute] });
    else group.keys.push(key.attribute);
  }
  return groups;
}

/** `SortBody` の props。 @graph-connects none */
interface SortBodyProps extends RuleCompareProps {
  /** 並べ替えの中身。 */
  body: SortBodyView;
}

/**
 * 並べ替えの読み方の断り — 母集合 ・ 値が無いとき ・ 同順位 ・ 出す件数を 1 本の文にする。値が無いときは
 * 扱いごとにキーを束ねる（キーごとに並べると、同じ扱いが繰り返されるだけで読む所が増える）。
 *
 * @param body 並べ替えの中身
 * @param t 表示言語への翻訳
 * @returns 「母集合: … ／ 値が無いとき: … ／ 同順位: … ／ 出す: …」
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 並べ替えの母集合・値が無いときの扱い・同順位・出す件数を、ⓘ に入れる 1 本の文にする。
 * @graph-connects groupWhenMissing [calls] 値が無いときの扱いごとのキーの束
 */
export function sortBodyNote(
  body: SortBodyView,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const population = displayLabel(body.population.label, t, body.population.untranslated);
  const whenMissing = groupWhenMissing(body.keys)
    .map((group) =>
      t('businessLogic.sortMissingGroup', {
        names: group.keys.map((key) => displayLabel(key.label, t, key.untranslated)).join(' ・ '),
        policy: t(`businessLogic.sortMissingValues.${group.policy}`),
      }),
    )
    .join(' ／ ');
  const take =
    body.take === null
      ? t('businessLogic.sortTakeAll')
      : t('businessLogic.sortTakeHead', { count: body.take });
  return [
    `${t('businessLogic.sortPopulation')}: ${population}`,
    `${t('businessLogic.sortWhenMissing')}: ${whenMissing}`,
    `${t('businessLogic.sortTieBreak')}: ${t(`businessLogic.sortTieBreaks.${body.tieBreak}`)}`,
    `${t('businessLogic.sortTake')}: ${take}`,
  ].join(' ／ ');
}

/**
 * 並べ替え。順位 ／ 属性 ／ 順 の表だけを出し、母集合 ・ 値が無いとき ・ 同順位 ・ 出す件数は「順位」の
 * 見出しの ⓘ に畳む（表の読み方の断りで、問いの答えではない。Dhik 2026-09-29「情報を絞る」）。
 * キーの決め方はここに出さない — 業務ルールの画面が畳みで出し、業務ルールのモーダルは画面への入口に任せる。
 *
 * 順は向き（数・日時）か値の並び（区分・状態・真偽）のどちらかで、値の並びは「→」で繋ぐ。
 * 案件の突き合わせでは、反対側に無い属性の行に地色を敷く。
 *
 * @param props 並べ替えの中身・描いている面・反対側の姿
 * @returns キー表と読み方の ⓘ
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 並べ替えのキー表を描き、母集合・値が無いとき・同順位・出す件数を ⓘ に畳み、案件で足した・外したキーに地色を敷く。
 * @graph-connects sortBodyNote [calls] 読み方の断りの文
 * @graph-connects InfoMark [calls] 読み方の断りの ⓘ
 */
export function SortBody({ body, side, counterpart }: SortBodyProps) {
  const { t } = useTranslation();
  const otherIds = sortAttributeIds(counterpart);
  return (
    <div className="flex flex-col gap-3">
      <table className="w-full border-collapse text-xs" data-rule-body="sort">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th scope="col" className={HEAD_CELL_CLASS}>
              {t('businessLogic.sortRank')}
              <span data-sort-take={body.take ?? 'all'}>
                <InfoMark text={sortBodyNote(body, t)} />
              </span>
            </th>
            <th scope="col" className={HEAD_CELL_CLASS}>
              {t('businessLogic.sortAttribute')}
            </th>
            <th scope="col" className={HEAD_CELL_CLASS}>
              {t('businessLogic.sortOrder')}
            </th>
          </tr>
        </thead>
        <tbody>
          {body.keys.map((key, index) => {
            const changed = isMissingOn(otherIds, key.attribute.id);
            return (
              <tr
                key={key.rank}
                data-changed={changed}
                className={`border-b border-[var(--color-border)]${specDiffCellClass(changed, side)}`}
              >
                <td className={`${VALUE_CELL_CLASS} ${MUTED_CLASS}`}>{index + 1}</td>
                <td className={VALUE_CELL_CLASS}>
                  <ElementRefText value={key.attribute} />
                </td>
                <td className="px-2 py-1 text-[var(--color-text-primary)]">
                  {Array.isArray(key.order) ? (
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {key.order.map((value, valueIndex) => (
                        <span key={value.id} className="inline-flex items-center gap-1">
                          {valueIndex > 0 && <span className={MUTED_CLASS}>→</span>}
                          <ElementRefText value={value} />
                        </span>
                      ))}
                    </span>
                  ) : (
                    t(`businessLogic.sortDirections.${key.order}`)
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
